import KeenSlider from 'ia-keen-slider';

if (!customElements.get('ia-slider')) {

  class IASlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.currentSlide = 0;
      this.currentSlideElement = null;
      this.prevButton = null;
      this.nextButton = null;
      this.progressFill = null;
      this.bulletElements = null;
      this.autoplay = this.getAttribute('data-auto-play') === 'true';
      this.autoplayDelay = parseInt(this.getAttribute('data-auto-play-delay') || '2000', 10);
      this.autoplayTimeout = null;
      this.mouseOver = false;
      this.loop = this.dataset.loop === 'true';
      this.vertical = this.dataset.vertical === 'true';

      // console.log('IASlider constructor', this);
    }
  
    connectedCallback() {
      // Defer initialization if data-defer-init is set
      if (this.dataset.deferInit === 'true') {
        return;
      }
      this.initializeSlideshow();
    }
  
    disconnectedCallback() {
      this.clearAutoplayTimeout();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.slider) {
        this.slider.destroy();
      }
    }
  
    initializeSlideshow() {
      const carousel = this.querySelector('.ia-slider__slides');
      // Controls may live outside (a section's header band names itself in
      // data-controls); the slider's own tree stays the default.
      const controlScope = (this.dataset.controls && document.getElementById(this.dataset.controls)) || this;
      this.currentSlideElement = controlScope.querySelector('.js-ia-slider-current') || this.querySelector('.js-ia-slider-current');
      this.prevButton = controlScope.querySelector('.js-ia-slider-prev');
      this.nextButton = controlScope.querySelector('.js-ia-slider-next');
      this.progressFill = this.querySelector('.ia-slider__progress-fill');
      this.bulletElements = this.querySelectorAll('[data-slide-index], .ia-slider__bullet, .js-ia-slider-bullet');
  
      if (!carousel) return;

      // getPropertyValue returns a string, and keen's arithmetic silently
      // does nothing with one.
      const num = (name, fallback) => {
        const raw = this.style.getPropertyValue(name).trim();
        // keen's own mode: the word has to reach it as the word.
        if (raw === 'auto') return 'auto';
        const value = parseFloat(raw);
        return Number.isFinite(value) ? value : fallback;
      };

      // Mobile start offset (fraction of the track); none keeps keen's 'auto'.
      const originMobile = parseFloat(this.dataset.originMobile);
      const mobileSlides = Number.isFinite(originMobile)
        ? this.offsetTrack(num('--items-mobile', 1), num('--spacing-mobile', 0), originMobile)
        : {
            perView: num('--items-mobile', 1),
            spacing: num('--spacing-mobile', 0),
            origin: this.dataset.centeredMobile === 'true' ? 0.25 : undefined,
          };

      const sliderConfig = {
        loop: this.loop,
        vertical: this.vertical,
        slides: {
          perView: num('--items-desktop', 1),
          spacing: num('--spacing-desktop', 0),
        },
        breakpoints: {
          '(max-width: 1023px)': {
            slides: mobileSlides,
          },
          '(min-width: 1600px)': {
            slides: {
              spacing: num('--spacing-desktop', 0),
              perView: num('--items-desktop-large', num('--items-desktop', 1)),
            },
          },
        },
        created: (slider) => {
          this.classList.add('is-initialized');
          const firstSlide = this.querySelector('.ia-slider__slide');
          if (firstSlide) {
            firstSlide.classList.add('is-active');
          }
          this.createProgressBar();
          this.initializeBullets();
          this.updateBullets();
          this.syncCardTracks();
          
          // Update progress after slider is created
          setTimeout(() => {
            this.updateProgress();
          }, 50);
        },
        slideChanged: (slider) => {
          this.updateProgress();
        }
      };

      // Enable loop for autoplay
      if (this.autoplay) {
        sliderConfig.loop = true;
      }

      // Initialize slider with autoplay plugin if enabled
      const plugins = this.autoplay ? [this.createAutoplayPlugin()] : [];
      this.slider = new KeenSlider(carousel, sliderConfig, plugins)
  
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.slider.prev();
        });
      }
  
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.slider.next();
        });
      }
  
      this.slider.on('slideChanged', (slide) => {
        const allSlides = this.querySelectorAll('.ia-slider__slide');
        allSlides.forEach(slide => slide.classList.remove('is-active'));
        
        const currentSlide = this.slider.track.details.rel;
        if (allSlides[currentSlide]) {
          allSlides[currentSlide].classList.add('is-active');
        }
        
        if (this.slider?.track?.details?.rel >= 0 && this.currentSlideElement) {
          this.currentSlideElement.textContent = this.slider.track.details.rel + 1;
        }
        
        this.updateBullets();
        this.updateProgress();
        this.syncCardTracks();
      });
      this.onResize = () => this.syncCardTracks();
      window.addEventListener('resize', this.onResize, { passive: true });

    }

    /* keen's per-slide track config (sizes as fractions of the track, as keen
       computes them for perView/spacing): the slides between the ends stop
       `offset` in, so the one before peeks; the first sits flush left and the
       last flush right, so the track never shows empty space at its ends. */
    offsetTrack(perView, spacing, offset) {
      return (size, slides) => {
        const gap = spacing / size || 0;
        const width = 1 / perView - gap + gap / perView;
        const last = slides.length - 1;
        return slides.map((_, index) => ({
          size: width,
          spacing: gap,
          origin: index === 0 ? 0 : index === last ? 1 - width : offset,
        }));
      };
    }

    /* A product card inside a slide carries its own image track, which keen
       yields to by its data-keen-slider-scrollable attribute plus an INLINE
       overflow-x: scroll (keen reads the inline style). Below the desktop
       breakpoint only the active slide, the one fully in view, keeps that
       scroll; the tracks of the slides peeking in from the side are closed,
       so a swipe on them moves the slider instead of a hidden gallery. The
       desktop keeps every track open: there the gallery is a hover, not a
       swipe. A single-image card has no scroll attribute and is left alone. */
    syncCardTracks() {
      const desktop = window.matchMedia('(min-width: 1024px)').matches;
      this.querySelectorAll('.ia-slider__slide').forEach((slide) => {
        const open = desktop || slide.classList.contains('is-active');
        slide.querySelectorAll('[data-card-track][data-keen-slider-scrollable]').forEach((track) => {
          track.style.overflowX = open ? 'scroll' : 'hidden';
        });
      });
    }
  
    initializeBullets() {
      if (!this.bulletElements || this.bulletElements.length === 0) {
        return;
      }

      this.bulletElements.forEach((bullet) => {
        const slideIndex = bullet.getAttribute('data-slide-index');
        if (slideIndex !== null) {
          bullet.addEventListener('click', () => {
            this.goToSlide(parseInt(slideIndex, 10));
          });
        }
      });
    }

    updateBullets() {
      if (!this.bulletElements || this.bulletElements.length === 0 || !this.slider) {
        return;
      }

      const currentSlide = this.slider.track.details.rel;
      
      this.bulletElements.forEach((bullet, index) => {
        const slideIndex = bullet.getAttribute('data-slide-index');
        const targetIndex = slideIndex !== null ? parseInt(slideIndex, 10) : index;
        
        if (targetIndex === currentSlide) {
          bullet.classList.add('is-active');
          bullet.setAttribute('aria-current', 'true');
        } else {
          bullet.classList.remove('is-active');
          bullet.setAttribute('aria-current', 'false');
        }
      });
    }
  
    createProgressBar() {
      if (this.dataset.showProgress !== 'true') return;

      if (this.querySelector('.ia-slider__progress')) return;

      const progressContainer = document.createElement('div');
      progressContainer.className = 'ia-slider__progress ' + this.dataset.colorSchemeUi;

      const progressBar = document.createElement('div');
      progressBar.className = 'ia-slider__progress-bar';

      const progressFill = document.createElement('div');
      progressFill.className = 'ia-slider__progress-fill';

      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressBar);
      this.appendChild(progressContainer);

      this.progressFill = progressFill;
    }

    updateProgress() {
      const d = this.slider?.track?.details;
      const totalSlides = d?.slides?.length || 0;

      // Having nothing to show is a normal state, not a fault: a track can be empty
      // while its content is still loading, or because the source of that content
      // failed. keen also leaves `details` null while it re-reads the track. All
      // three reach this line, so `d.rel` must never be read without this guard.
      if (!d || totalSlides === 0) {
        this.prevButton?.classList.add('ia-visibility-hidden');
        this.nextButton?.classList.add('ia-visibility-hidden');
        return;
      }

      const currentSlide = d.rel;
      this.currentSlide = currentSlide;
      const minIdx = typeof d.minIdx === 'number' ? d.minIdx : 0;
      const maxIdx = typeof d.maxIdx === 'number' ? d.maxIdx : (totalSlides - 1);
      const steps = Math.max(1, (maxIdx - minIdx + 1));
      const atStart = currentSlide <= minIdx;
      const atEnd   = currentSlide >= maxIdx;
      if (this.dataset.showProgress === 'true' && this.progressFill && this.slider) {
        
        // Only update if we have valid data
        if (totalSlides > 0) {
          // const progress = ((currentSlide + 1) / totalSlides) * 100;
          // this.progressFill.style.width = `${Math.min(progress, 100)}%`;
          
          // Progress by "page" (works with perView > 1)
          const page = currentSlide - minIdx + 1;
          const progress = Math.min(100, (page / steps) * 100);
          this.progressFill.style.width = `${progress}%`;
        }
      }

      if (this.prevButton) {
        this.prevButton.classList.toggle('ia-visibility-hidden', atStart);
      }

      if (this.nextButton) {
        this.nextButton.classList.toggle('ia-visibility-hidden', atEnd);
      }
    }
  
    next() {
      if (this.slider) {
        this.slider.next();
      }
    }
  
    prev() {
      if (this.slider) {
        this.slider.prev();
      }
    }
  
    goToSlide(index, skipAnimation = false) {
      if (this.slider) {
        if (skipAnimation) {
          this.slider.moveToIdx(index, true, { duration: 0 });
        }
        else {
          this.slider.moveToIdx(index);
        }
      }
    }
  
    createAutoplayPlugin() {
      return (slider) => {
        const clearNextTimeout = () => {
          this.clearAutoplayTimeout();
        };

        const nextTimeout = () => {
          clearNextTimeout();
          if (this.mouseOver) return;
          this.autoplayTimeout = setTimeout(() => {
            if (this.slider) {
              this.slider.next();
            }
          }, this.autoplayDelay);
        };

        slider.on('created', () => {
          this.addEventListener('mouseenter', () => {
            this.mouseOver = true;
            clearNextTimeout();
          });

          this.addEventListener('mouseleave', () => {
            this.mouseOver = false;
            nextTimeout();
          });

          nextTimeout();
        });

        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      };
    }

    clearAutoplayTimeout() {
      if (this.autoplayTimeout) {
        clearTimeout(this.autoplayTimeout);
        this.autoplayTimeout = null;
      }
    }

    destroy() {
      this.clearAutoplayTimeout();
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
    }
  }
  
  
  customElements.define('ia-slider', IASlider);
}